/**
 * `topdown-2d` grammar + validators (design §15). Conservative like the autorunner's:
 * corridor fit, room fit, camera legibility, pacing — and the lock-and-key REACHABILITY
 * FIXPOINT: a locked corridor opens only once its key's room is already reachable; iterate
 * until stable. Goal unreachable at the fixpoint ⇒ unbeatable (catches key-behind-its-own-door).
 */
import type { LevelFinding } from '../../types.js';
import type { TopdownThreeCs } from '../../three-cs.js';
import type { TopdownLevel } from './schema.js';

export const DEFAULT_TOPDOWN_CLEARANCE = 0.1;

export interface TopdownGrammar {
  minCorridorWidth: number; // 2*bodyRadius + clearance
  minRoomSide: number; // 2*bodyRadius + clearance
  moveSpeed: number;
  viewW: number;
  viewH: number;
}

export function compileTopdownGrammar(p: TopdownThreeCs): TopdownGrammar {
  const clearance = p.solver?.clearanceMargin ?? DEFAULT_TOPDOWN_CLEARANCE;
  return {
    minCorridorWidth: 2 * p.character.bodyRadius + clearance,
    minRoomSide: 2 * p.character.bodyRadius + clearance,
    moveSpeed: p.character.moveSpeed,
    viewW: p.camera.viewW,
    viewH: p.camera.viewH,
  };
}

/**
 * Lock-and-key reachability fixpoint. Returns the set of reachable room ids and the
 * corridor indices that stay locked forever (their key is never reachable).
 */
export function reachabilityFixpoint(level: TopdownLevel): {
  reachable: Set<string>;
  foreverLocked: number[];
} {
  const start = level.rooms.find((r) => r.kind === 'start');
  const reachable = new Set<string>();
  if (!start) return { reachable, foreverLocked: [] };
  reachable.add(start.id);
  const keyRoom = new Map(level.keys.map((k) => [k.id, k.roomId]));

  let grew = true;
  while (grew) {
    grew = false;
    for (const c of level.corridors) {
      const open =
        c.lockedBy === undefined ||
        (keyRoom.has(c.lockedBy) && reachable.has(keyRoom.get(c.lockedBy)!));
      if (!open) continue;
      const fromIn = reachable.has(c.fromId);
      const toIn = reachable.has(c.toId);
      if (fromIn && !toIn) {
        reachable.add(c.toId);
        grew = true;
      } else if (toIn && !fromIn) {
        reachable.add(c.fromId);
        grew = true;
      }
    }
  }

  const foreverLocked: number[] = [];
  level.corridors.forEach((c, i) => {
    if (c.lockedBy === undefined) return;
    const kr = keyRoom.get(c.lockedBy);
    if (!kr || !reachable.has(kr)) foreverLocked.push(i);
  });
  return { reachable, foreverLocked };
}

/** Rough traversal estimate: half-perimeter of each reachable room ÷ moveSpeed. */
function estimateTraversalSeconds(
  level: TopdownLevel,
  reachable: Set<string>,
  g: TopdownGrammar,
): number {
  let dist = 0;
  for (const r of level.rooms) {
    if (reachable.has(r.id)) dist += (r.w + r.h) / 2;
  }
  return dist / g.moveSpeed;
}

export function validateTopdown(level: TopdownLevel, g: TopdownGrammar): LevelFinding[] {
  const findings: LevelFinding[] = [];

  // Per-room fit + legibility.
  for (const r of level.rooms) {
    if (r.w < g.minRoomSide || r.h < g.minRoomSide) {
      findings.push({
        severity: 'error',
        code: 'room-too-small',
        message: `room ${r.id} (${r.w}×${r.h}m) is smaller than the body needs (${g.minRoomSide.toFixed(2)}m)`,
        at: { roomId: r.id },
      });
    }
    if (r.w > g.viewW && r.h > g.viewH) {
      findings.push({
        severity: 'warn',
        code: 'room-exceeds-camera',
        message: `room ${r.id} (${r.w}×${r.h}m) exceeds the camera view (${g.viewW}×${g.viewH}m) in both axes`,
        at: { roomId: r.id },
      });
    }
  }

  // Corridor fit.
  level.corridors.forEach((c, i) => {
    if (c.width < g.minCorridorWidth) {
      findings.push({
        severity: 'error',
        code: 'corridor-too-narrow',
        message: `corridor ${c.fromId}→${c.toId} is ${c.width}m wide; the body needs ${g.minCorridorWidth.toFixed(2)}m`,
        at: { roomId: c.toId, corridorIndex: i },
      });
    }
  });

  // Lock-and-key reachability.
  const { reachable, foreverLocked } = reachabilityFixpoint(level);
  for (const i of foreverLocked) {
    const c = level.corridors[i];
    findings.push({
      severity: 'error',
      code: 'door-key-unreachable',
      message: `the key for the locked corridor ${c.fromId}→${c.toId} can never be picked up`,
      at: { roomId: c.toId, corridorIndex: i },
    });
  }
  for (const r of level.rooms) {
    if (reachable.has(r.id)) continue;
    findings.push(
      r.kind === 'goal'
        ? {
            severity: 'error',
            code: 'goal-unreachable',
            message: `goal room ${r.id} can never be reached from the start`,
            at: { roomId: r.id },
          }
        : {
            severity: 'warn',
            code: 'room-unreachable',
            message: `room ${r.id} can never be reached from the start`,
            at: { roomId: r.id },
          },
    );
  }

  // Pacing (advisory).
  const est = estimateTraversalSeconds(level, reachable, g);
  if (est < level.targetSeconds * 0.4) {
    findings.push({
      severity: 'warn',
      code: 'pacing-under-target',
      message: `rough traversal ≈${est.toFixed(0)}s is well under the ${level.targetSeconds}s target — consider more rooms or bigger spaces`,
    });
  }

  return findings;
}
